import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { SystemHealthDto } from "../models/responses/SystemHealthDto";

export interface SystemHealthRepository {
  /**
   * Aggregated health snapshot (GET admin/system-health). Polled with the same
   * house pattern as the import-status screen: 60s refetchInterval, paused
   * while the tab is unfocused, plus the screen's manual refresh button.
   */
  useSystemHealth(): Resource<SystemHealthDto>;
  /** Soft-cancels one stranded order and refreshes the health snapshot. */
  cancelStrandedOrder(orgId: number, orderId: number): Promise<void>;
}

const SYSTEM_HEALTH_KEY = ["system-health"];

export function useSystemHealthRepository(): SystemHealthRepository {
  const apiClient = useApiClient();

  function useSystemHealth(): Resource<SystemHealthDto> {
    return useQuery(SYSTEM_HEALTH_KEY, () => apiClient.getSystemHealth(), {
      refetchInterval: 60000,
      staleTime: 55000,
    });
  }

  const queryClient = useQueryClient();

  async function cancelStrandedOrder(orgId: number, orderId: number): Promise<void> {
    await apiClient.cancelOrder(orgId, orderId);
    await queryClient.invalidateQueries({ queryKey: SYSTEM_HEALTH_KEY });
  }

  return { useSystemHealth, cancelStrandedOrder };
}
